import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import RAGsTable from "./RAGsTable"; // Using the new RAGs table component
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Interface reflects the data structure for a RAG entry
interface RAG {
    id: string;
    name: string;
    status: string;
    projectId: string;
    databaseType: string;
    embeddingModelId: string;
    applicationName?: string;
    cloudProvider: string;
    solutionOwner?: SolutionOwnerInfo | null;
    createdAt: string;
    updatedAt: string;
    environment?: string;
    solutionType?: string;
    reasonForRejection?: string;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof RAG | string;
    groupBy: string;
}

let searchOptionsMasterList: SearchOptions[] = [];

const RAGPage: React.FC = () => {
    const [allRAGs, setAllRAGs] = useState<RAG[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);

    const initialColumnFilters: Record<string, boolean> = {
        name: true,
        projectId: true,
        databaseType: true,
        embeddingModelId: true,
        cloudProvider: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetRAGs {
                      getRAGs {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        cloudProvider
                        environment
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          rag {
                            projectId
                            databaseType
                            embeddingModelId
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        application {
                          id
                          applicationName
                        }
                      }
                    }
                `);

                // **FIXED HERE**: Correctly access the response data with a fallback
                const ragsData = response.data?.getRAGs || response.getRAGs || [];

                if (ragsData) {
                    const processedRAGs = ragsData.map((rag: any): RAG => ({
                        id: rag.id,
                        name: rag.name || "Unknown RAG",
                        status: (rag.status || 'pending').toLowerCase(),
                        projectId: rag.details?.rag?.projectId || "N/A",
                        databaseType: rag.details?.rag?.databaseType || "N/A",
                        embeddingModelId: rag.details?.rag?.embeddingModelId || "N/A",
                        applicationName: rag.application?.applicationName,
                        cloudProvider: rag.cloudProvider || "N/A",
                        solutionOwner: rag.solutionOwner,
                        createdAt: rag.createdAt,
                        updatedAt: rag.updatedAt,
                        environment: rag.environment,
                        solutionType: rag.solutionType,
                        reasonForRejection: rag.reasonForRejection,
                    }));
                    setAllRAGs(processedRAGs);

                    searchOptionsMasterList = processedRAGs
                        .map((rag: RAG) => [
                            { category: "name", groupBy: "RAG NAME", chipLabel: `RAG: ${rag.name}`, dropdownLabel: rag.name },
                            { category: "projectId", groupBy: "PROJECT ID", chipLabel: `Project ID: ${rag.projectId}`, dropdownLabel: rag.projectId },
                            { category: "databaseType", groupBy: "DATABASE TYPE", chipLabel: `DB: ${rag.databaseType}`, dropdownLabel: rag.databaseType },
                            { category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${rag.cloudProvider}`, dropdownLabel: rag.cloudProvider },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${rag.status}`, dropdownLabel: rag.status.charAt(0).toUpperCase() + rag.status.slice(1) },
                            ...(rag.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "RESOURCE OWNER", chipLabel: `Owner: ${rag.solutionOwner.name}`, dropdownLabel: rag.solutionOwner.name }] : []),
                        ])
                        .flat();

                    searchOptionsMasterList = searchOptionsMasterList.filter(
                        (obj, index, self) =>
                            obj.dropdownLabel &&
                            index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel)
                    );
                    searchOptionsMasterList.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = a.dropdownLabel.toUpperCase();
                        const labelB = b.dropdownLabel.toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                }
            } catch (error) {
                console.error("Error fetching RAGs:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'name') return 'RAG Name';
        if (key === 'solutionOwner') return 'Resource Owner';
        if (key === 'createdAt') return 'Added On';
        if (key === 'databaseType') return 'Database Type';
        if (key === 'embeddingModelId') return 'Embedding Model';
        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredRAGs =
        searchQuery.length === 0
            ? allRAGs
            : allRAGs.filter((rag: RAG) => {
                return searchQuery.every(queryParam => {
                    let ragValue: any;
                    if (queryParam.category === "solutionOwner.name") {
                        ragValue = rag.solutionOwner?.name;
                    } else {
                        ragValue = rag[queryParam.category as keyof RAG];
                    }
                    if (ragValue === null || ragValue === undefined) return false;
                    return String(ragValue).toLowerCase().includes(queryParam.dropdownLabel.toLowerCase());
                });
            });

    const pendingRAGs = globallyFilteredRAGs.filter(rag => rag.status === 'pending');
    const activeRAGs = globallyFilteredRAGs.filter(rag => rag.status === 'active' || rag.status === 'approved');
    const rejectedRAGs = globallyFilteredRAGs.filter(rag => rag.status === 'rejected');

    const totalRAGsOverall = allRAGs.length;
    const totalMatchingSearch = globallyFilteredRAGs.length;

    const canAddRAG = true;

    const handleAddRAG = () => {
        navigate("/aisolutions/rag/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"aisolutions-rag"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"aisolutions-rag"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx:2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                RAG Solutions
                            </Typography>
                            <Box sx={{display:'flex', justifyContent:'center'}}>
                                <Box>
                            {canAddRAG && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "black", color: "white", padding: "8px 24px",
                                        fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                         '&:hover': { backgroundColor: '#333333' }
                                    }}
                                    onClick={handleAddRAG}
                                    disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                >
                                    Add New RAG
                                </Button>
                                
                            )}
                            </Box>
                            <IconButton
                                aria-label="Help" 
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{ padding: 0, border: 'none', ml:2, '&:hover': { backgroundColor: 'action.hover' } }}
                            >
                                <Box
                                    component="img"
                                    src="/assets/Help.svg" 
                                    alt="" 
                                    sx={{ width: 40, height: 40, display: 'block' }}
                                />
                            </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by RAG name, project, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                sx={{
                                    border: "1px solid #bbb",
                                    borderRadius: "10px",
                                    height: "45px",
                                    width: "45px",
                                }}
                                onClick={handleColumnFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{xs: 1, sm:2}} sx={{minHeight:'70vh'}}>
                        {totalRAGsOverall > 0 && totalMatchingSearch > 0 &&(
                            <>
                        <Accordion
                            defaultExpanded={pendingRAGs.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={pendingRAGs.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending RAGs ({pendingRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {pendingRAGs.length > 0 ? (
                                    <RAGsTable
                                        rags={pendingRAGs}
                                        columnFilters={columnFilters}
                                        statusType="pending"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No pending RAGs match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={activeRAGs.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={activeRAGs.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active RAGs ({activeRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {activeRAGs.length > 0 ? (
                                    <RAGsTable
                                        rags={activeRAGs}
                                        columnFilters={columnFilters}
                                        statusType="active"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No active RAGs match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={rejectedRAGs.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={rejectedRAGs.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected RAGs ({rejectedRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {rejectedRAGs.length > 0 ? (
                                    <RAGsTable
                                        rags={rejectedRAGs}
                                        columnFilters={columnFilters}
                                        statusType="rejected"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No rejected RAGs match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        </>)}
                         {totalRAGsOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No RAGs Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No RAGs match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalRAGsOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
                                    //height:"70vh"
                                 
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No RAGs" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No RAGs are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first RAG Solution.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default RAGPage;

// import { Box, Typography } from "@mui/material";
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import DashBoardPage from "../DashBoardPage";
// import MainDrawer from "../MainDrawer";
// import ComingSoonPage from "../ComingSoonPage";
// const RAG: React.FC = () => {
//     return(<Box sx={{ display: "flex"}}>
//     <MainDrawer index={"aisolutions-rag"} />
// {/* Helllo */}
//     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100%", width:"100%"}}>
//  {/* <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography> */}
//  <ComingSoonPage/>
//     </Box>
//     </Box>)
// }
// export default RAG;
    
